<template>
  <div
    class="column gap-1 select-wrapper"
    @keydown="handleKeydown"
    tabindex="0"
    :class="{ disabled: disabled }"
  >
    <label
      class="body xs font-regular row gap-1 align-center select-label"
      v-if="label"
      for="select"
    >
      {{ label }}
      <p v-if="required" class="body xs" :class="{ required: !disabled }">*</p>
      <CustomTooltip v-if="aditionalInfo" :message="aditionalInfo">
        <AppIcon name="info" size="body-s" className="aditional-info" />
      </CustomTooltip>
    </label>
    <div
      class="custom-select"
      role="listbox"
      :class="{ active: showOptions, error: error }"
      @click="toggleOptions"
      ref="customSelectRef"
    >
      <p
        class="text-value"
        :class="{
          'color-lighter': internalValue === undefined || internalValue === null
        }"
      >
        {{
          internalValue === undefined || internalValue === null
            ? placeHolder
            : options.find((option) => option.value === internalValue)?.label
        }}
      </p>
      <div class="chevron-icon">
        <AppIcon name="chevron-down" />
      </div>
      <div
        v-if="showOptions"
        class="select-options"
        :style="{ right: optionsRight }"
      >
        <div
          v-for="(option, index) in options"
          :key="option.value"
          class="option"
          role="listitem"
          :class="{ focused: index === focusedIndex }"
          @click="selectOption(option.value)"
          @mouseenter="clearFocusedIndex"
        >
          <AppIcon
            v-if="option.icon"
            :name="option.icon.name"
            :size="option.icon.size"
          />
          <p>{{ option.label }}</p>
        </div>
      </div>
    </div>
    <div v-if="error && errorMessage" class="row gap-1 error-message">
      <AppIcon name="info" size="body-s" />
      <p class="body xs font-regular">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import CustomTooltip from '@/components/common/tooltip/CustomTooltip.vue';
import { FormSelectOption } from '@/models/select-props-interfaces';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  label?: string;
  placeHolder?: string;
  modelValue?: string | number;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  aditionalInfo?: string;
  disabled?: boolean;
  options: FormSelectOption[];
}>();

const emit = defineEmits(['update:modelValue']);

const showOptions = ref(false);
const internalValue = ref(props.modelValue);
const focusedIndex = ref<number | undefined>(undefined);
const customSelectRef = ref<HTMLElement | null>(null);
const optionsRight = ref<'0' | 'auto'>('0');

function toggleOptions() {
  if (props.disabled) return;
  showOptions.value = !showOptions.value;
  if (!showOptions.value) focusedIndex.value = undefined;
}

function emitSelection() {
  emit('update:modelValue', internalValue.value);
}

function selectOption(value: string | number) {
  internalValue.value = value;
  emitSelection();
  showOptions.value = false;
  (document.activeElement as HTMLElement)?.blur();
}

function handleOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.custom-select')) {
    showOptions.value = false;
    focusedIndex.value = undefined;
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!showOptions.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOptions();
    }
    return;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusedIndex.value =
      focusedIndex.value === undefined
        ? 0
        : (focusedIndex.value + 1) % props.options.length;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusedIndex.value =
      focusedIndex.value === undefined
        ? props.options.length - 1
        : (focusedIndex.value - 1 + props.options.length) %
          props.options.length;
  } else if (event.key === 'Enter' && focusedIndex.value !== undefined) {
    event.preventDefault();
    selectOption(props.options[focusedIndex.value].value);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    showOptions.value = false;
    focusedIndex.value = undefined;
  }
}

function clearFocusedIndex() {
  focusedIndex.value = undefined;
}

function handleResize() {
  if (!customSelectRef.value) return;
  optionsRight.value = customSelectRef.value.offsetWidth < 80 ? 'auto' : '0';
}

if (!props.disabled) {
  watch(
    () => props.modelValue,
    (newValue) => {
      internalValue.value = newValue;
    }
  );
  onMounted(() => {
    document.addEventListener('click', handleOutsideClick);
    if (customSelectRef.value) {
      const resizeObserver = new ResizeObserver(() => handleResize());
      resizeObserver.observe(customSelectRef.value);
    }
    handleResize();
  });
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleOutsideClick);
  });
}
</script>

<style scoped lang="scss">
.select-wrapper {
  text-align: start;

  &:focus {
    outline: none;

    .custom-select {
      border: 2px solid $border-color-focus;
      transition: 0.2s ease;
    }
  }

  &.disabled {
    color: $color-text-disabled !important;

    .custom-select {
      cursor: not-allowed;
      border: 1px solid $border-color-disabled;
      background-color: $border-color-disabled;
    }
  }
}

.select-label {
  .required {
    color: $border-color-error;
  }
}

.custom-select {
  border: 1px solid $border-color-base;
  border-radius: $radius-3;
  padding: $space-2 $space-3;
  display: flex;
  flex-direction: row;
  gap: $space-1;
  align-items: center;
  color: $color-neutral-700;
  font-weight: $fw-regular;
  font-size: $body-s-font-size;
  user-select: none;
  cursor: pointer;
  position: relative;

  &:hover {
    border: 1px solid $border-color-hover;
    transition: 0.2s ease;
  }

  &.active {
    transition: 0.2s ease;
    border: 2px solid $border-color-focus;

    .chevron-icon {
      transform: rotate(180deg);
      color: $border-color-focus;
    }
  }

  &.error {
    border: 1px solid $border-color-error !important;
    transition: 0.2s ease;
  }

  &:focus {
    outline: none;
  }

  .text-value {
    flex: 1;
  }

  .chevron-icon {
    flex: 0 0 auto;
    transition: 0.2s ease;
    color: $border-color-base;
  }
}

.select-options {
  position: absolute;
  top: 110%;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: $color-neutral-0;
  border: 1px solid $color-neutral-100;
  border-radius: $radius-3;
  padding: $space-2 $space-1;
  display: flex;
  flex-direction: column;
  gap: $space-1;
  max-height: 33vh;
  overflow-y: auto;
  box-shadow: $elevation-2;
  user-select: none;
  cursor: pointer;

  .option {
    padding: $space-2 $space-3;
    transition: 0.2s ease;
    border-radius: $radius-2;
    display: flex;
    flex-direction: row;
    gap: $space-1;
    align-items: center;

    &:hover {
      background-color: $color-secondary-800;
      color: $color-neutral-0;
      transition: 0.2s ease;
    }

    &.focused {
      background-color: $color-secondary-800;
      color: $color-neutral-0;
      transition: 0.2s ease;
    }
  }
}

.error-message {
  color: $border-color-error;
}

.aditional-info {
  color: $border-color-base;
}
</style>
